import Text from "@src/lib/ui/Text";
import { FC } from "react";
import { gql } from "@apollo/client";
import {
  useMyCompletedTrainingTableQuery,
  useMyCompletedTrainingTableLazyQuery,
  MyCompletedTrainingTable_AssignmentFragment,
} from "./MyCompletedTrainingTable.generated";
import { useRouter } from "next/router";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import ContentAvatar from "@src/features/contentLibrary/ContentAvatar";
import {
  AssignmentContentType,
  AssignmentsInput,
  LibraryItemType,
} from "@src/types.generated";
import { formatDateShort } from "@src/features/myTraining/utils";
import { useMyTrainingPageContentTranslations } from "@src/features/myTraining/hooks/useMyTrainingPageContentTranslations";
import useUser from "@src/features/app/hooks/useUser";

type Column = "name" | "type" | "completedAt";

const contentTypes = [
  AssignmentContentType.Course,
  AssignmentContentType.Module,
  AssignmentContentType.ScormCourse,
];

type Props = {
  filterBarContainer?: HTMLElement | null;
};

const MyCompletedTrainingTable: FC<Props> = ({ filterBarContainer }) => {
  const { user } = useUser();
  const router = useRouter();
  const translations = useMyTrainingPageContentTranslations();

  const input: AssignmentsInput = {
    filters: {
      userId: { value: user?.id || -1 },
      statuses: { value: ["complete"] },
      contentTypes: {
        value: contentTypes,
      },
      isTopLevel: { value: true },
      includeCompletedAssignmentsWithoutAccessSource: { value: true },
    },
  };

  const allColumns: Column[] = ["name", "type", "completedAt"];

  return (
    <PaginatedTable<
      MyCompletedTrainingTable_AssignmentFragment,
      Column,
      typeof useMyCompletedTrainingTableQuery,
      typeof useMyCompletedTrainingTableLazyQuery,
      Column
    >
      input={input}
      queryHook={useMyCompletedTrainingTableQuery}
      lazyQueryHook={useMyCompletedTrainingTableLazyQuery}
      getItems={(data) => data?.Assignments?.objects ?? []}
      getTotalItemCount={(data) => data?.Assignments?.totalCount ?? 0}
      noDataEmptyState={
        <div className="flex flex-1 self-stretch">
          <EmptyState>
            <EmptyStateIcon>
              <CheckmarkIcon className="size-16" />
            </EmptyStateIcon>
            <EmptyStateTitle>No completed training yet</EmptyStateTitle>
          </EmptyState>
        </div>
      }
      hideCount
      filterBarContainer={filterBarContainer}
      horizontalScroll
      stickyFirstColumn
      resizableColumns
      columnWidthPersistenceKey="my-training-completed-v1"
      trailingEmptyColumnWidth={16}
      growableColumn="name"
      allColumns={allColumns}
      defaultSort={{ column: "completedAt", descending: true }}
      columnTitles={{
        name: translations.name,
        type: translations.type,
        completedAt: "Completed",
      }}
      exportConfig={{
        title: "my-completed-training.csv",
        exportColumns: () => allColumns,
        exportColumnTitles: {
          name: translations.name,
          type: translations.type,
          completedAt: translations.completedAt,
        },
        exportFns: {
          name: (item) => item.libraryItem.name.myTranslation,
          type: (item) => {
            if (item.libraryItem.type === LibraryItemType.Module)
              return translations.module;
            if (item.libraryItem.type === LibraryItemType.ScormCourse)
              return translations.scormCourse;
            return translations.course;
          },
          completedAt: (item) =>
            formatDateShort(
              item.currentCourseInstance?.completedAt ||
                item.currentModuleInstance?.completedAt ||
                item.currentSCORMCourseInstance?.completedAt,
            ),
        },
      }}
      responsiveness={{
        name: {
          minWidth: 240,
          flex: 3,
        },
        type: {
          minWidth: 100,
        },
        completedAt: {
          minWidth: 120,
        },
      }}
      onRowClick={(item) => {
        if (item.currentCourseInstance) {
          router.push({
            pathname: "/my-training/course-instance/[id]",
            query: { id: String(item.currentCourseInstance.id) },
          });
        } else if (item.currentModuleInstance) {
          router.push({
            pathname: "/my-training/module-instance/[id]",
            query: { id: String(item.currentModuleInstance.id) },
          });
        } else if (item.currentSCORMCourseInstance) {
          router.push({
            pathname: "/my-training/scorm-course-instance/[id]",
            query: { id: String(item.currentSCORMCourseInstance.id) },
          });
        }
      }}
      renderFns={{
        name: (item) => (
          <div className="flex items-center gap-2">
            <ContentAvatar
              size={32}
              contentType="Module"
              style={{ borderRadius: 8 }}
              coverImage={item.libraryItem.coverImage}
              defaultBackgroundHashKey={item.libraryItem.id}
            />
            <Text type="P2">{item.libraryItem.name.myTranslation}</Text>
          </div>
        ),
        type: (item) => {
          if (item.libraryItem.type === LibraryItemType.Module) {
            return translations.module;
          } else if (item.libraryItem.type === LibraryItemType.ScormCourse) {
            return translations.scormCourse;
          }
          return translations.course;
        },
        completedAt: (item) =>
          formatDateShort(
            item.currentCourseInstance?.completedAt ||
              item.currentModuleInstance?.completedAt ||
              item.currentSCORMCourseInstance?.completedAt,
          ),
      }}
      itemId={(item) => item.id}
    />
  );
};

export default MyCompletedTrainingTable;

gql`
  query MyCompletedTrainingTable(
    $input: AssignmentsInput!
    $pagination: PaginationInput
  ) {
    Assignments(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...MyCompletedTrainingTable_Assignment
      }
    }
  }
  fragment MyCompletedTrainingTable_Assignment on Assignment {
    id
    status
    libraryItem {
      ...MyCompletedTrainingTable_LibraryItem
    }
    currentCourseInstance {
      ...MyCompletedTrainingTable_CourseInstance
    }
    currentModuleInstance {
      ...MyCompletedTrainingTable_ModuleInstance
    }
    currentSCORMCourseInstance {
      ...MyCompletedTrainingTable_SCORMCourseInstance
    }
  }
  fragment MyCompletedTrainingTable_LibraryItem on LibraryItem {
    id
    type
    name {
      ...MyTranslationSet
    }
    coverImage {
      ...CoverImage
    }
  }
  fragment MyCompletedTrainingTable_ModuleInstance on ModuleInstance {
    id
    completedAt
  }
  fragment MyCompletedTrainingTable_CourseInstance on CourseInstance {
    id
    completedAt
  }
  fragment MyCompletedTrainingTable_SCORMCourseInstance on SCORMCourseInstance {
    id
    completedAt
  }
`;
